import { CALLING_POC_OPTIONS } from "../../../../constants/callingPocOptions";
import { CALLING_RESPONSE_STATUS_OPTIONS } from "../../../../constants/callingResponseStatusOptions";
import { CALLING_STATUS_OPTIONS } from "../../../../constants/submissionSummaryOptions";
import { PharmaStpFileModel } from "../../../../data-model/nycbsPharmaOrder";
import { CallingStatus } from "../../../../enums/callingStatus";
import { CmmResponseStatus } from "../../../../enums/cmmResponseStatus";
import { PocAssignee, PocAssigneeLabels } from "../../../../enums/pocAssignee";
import {
  resolveSelectOptionValue,
  SelectOption,
} from "./resolveSelectOptionValue";

const POC_SELECT_OPTIONS: SelectOption[] = CALLING_POC_OPTIONS.map((opt) => ({
  label: opt.text,
  value: opt.id,
}));

const RESPONSE_STATUS_SELECT_OPTIONS: SelectOption[] =
  CALLING_RESPONSE_STATUS_OPTIONS.map((opt) => ({
    label: opt.text,
    value: opt.id,
  }));

const CASE_STATUS_SELECT_OPTIONS: SelectOption[] = CALLING_STATUS_OPTIONS.map(
  (opt) => ({
    label: opt.text,
    value: opt.id,
  }),
);

/**
 * Resolves a POC Firebase UID to a dropdown value, using the assignee label when
 * the id is known but not present in the static calling POC list.
 *
 * @param rawPoc - `non_epa_poc` or fallback `poc` from the order row
 * @returns Resolved POC option value
 */
const resolveCallingPocValue = (rawPoc: string): string => {
  const trimmed = rawPoc.trim();
  if (!trimmed) {
    return "";
  }

  const fromOptions = resolveSelectOptionValue(POC_SELECT_OPTIONS, trimmed);
  if (POC_SELECT_OPTIONS.some((option) => option.value === fromOptions)) {
    return fromOptions;
  }

  const matchingAssignee = Object.values(PocAssignee).find(
    (assigneeId) => assigneeId === trimmed,
  );
  if (matchingAssignee) {
    return matchingAssignee;
  }

  return trimmed;
};

/** Form state for the calling-summary submission form. */
export interface CallingFormData {
  callingPoc: string;
  caseId: string;
  repName: string;
  phoneNumber: string;
  faxNumber: string;
  sentTo: string;
  comment: string;
  responseStatus: string;
  caseStatus: string;
  nextAction: string;
  followUpDate: string;
  callRequired: string;
}

/**
 * Builds initial calling-summary form values from a worklist order row.
 *
 * @param data - Raw order row from the calling worklist API
 * @returns Prefilled form values for the calling summary form
 */
export const buildCallingFormDataFromOrder = (
  data: PharmaStpFileModel,
): CallingFormData => {
  return {
    callingPoc: resolveCallingPocValue(data.non_epa_poc ?? data.poc ?? ""),
    // Case ID is the payer's reference, entered by the user — never the CMM ID.
    caseId: data.non_epa_case_id ?? "",
    repName: data.non_epa_insurance_rep ?? "",
    phoneNumber: data.non_epa_reachout_number ?? "",
    faxNumber: "",
    sentTo: "",
    comment: data.non_epa_comment ?? "",
    // The popup always opens on Pending, whatever the order currently holds;
    // the user picks the real outcome before saving.
    responseStatus: CmmResponseStatus.PENDING,
    caseStatus: resolveSelectOptionValue(
      CASE_STATUS_SELECT_OPTIONS,
      data.non_epa_status,
      CallingStatus.UNRESOLVED,
    ),
    nextAction: "",
    followUpDate: "",
    callRequired: data.non_epa_required ?? "",
  };
};

/**
 * Returns POC dropdown options, injecting the current row value when it is not
 * in the static calling POC list.
 *
 * @param currentPoc - Resolved POC value for the open row
 * @returns Select options for the Calling POC field
 */
export const getCallingPocSelectOptions = (
  currentPoc: string,
): SelectOption[] => {
  if (!currentPoc) {
    return POC_SELECT_OPTIONS;
  }

  if (POC_SELECT_OPTIONS.some((option) => option.value === currentPoc)) {
    return POC_SELECT_OPTIONS;
  }

  const label = PocAssigneeLabels[currentPoc as PocAssignee] ?? currentPoc;

  return [{ label, value: currentPoc }, ...POC_SELECT_OPTIONS];
};

/**
 * Returns response-status options, including the current row value when needed.
 *
 * @param currentStatus - Resolved response status for the open row
 * @returns Select options for the Calling Response Status field
 */
export const getResponseStatusSelectOptions = (
  currentStatus: string,
): SelectOption[] => {
  if (
    !currentStatus ||
    RESPONSE_STATUS_SELECT_OPTIONS.some(
      (option) => option.value === currentStatus,
    )
  ) {
    return RESPONSE_STATUS_SELECT_OPTIONS;
  }

  return [
    { label: currentStatus, value: currentStatus },
    ...RESPONSE_STATUS_SELECT_OPTIONS,
  ];
};

/**
 * Returns case-status options, including the current row value when needed.
 *
 * @param currentStatus - Resolved calling status for the open row
 * @returns Select options for the Case Status field
 */
export const getCaseStatusSelectOptions = (
  currentStatus: string,
): SelectOption[] => {
  if (
    !currentStatus ||
    CASE_STATUS_SELECT_OPTIONS.some((option) => option.value === currentStatus)
  ) {
    return CASE_STATUS_SELECT_OPTIONS;
  }

  return [
    { label: currentStatus, value: currentStatus },
    ...CASE_STATUS_SELECT_OPTIONS,
  ];
};
